import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Plus, Trash2, FileText, Printer } from "lucide-react";
import { inr2, today } from "@/lib/format";
import { printInvoice } from "@/lib/print";
import { toast } from "sonner";

const newItem = () => ({
  _id: Math.random().toString(36).slice(2),
  cn_no: "",
  cn_date: "",
  from: "",
  to: "",
  vehicle_type: "",
  weight: "",
  vehicle_no: "",
  inv_no: "",
  inv_date: "",
  dc_no: "",
  dc_date: "",
  amount: 0,
});

const composeDesc = (it) => {
  let d = `Freight Charges Against CN No.${it.cn_no || ""}${it.cn_date ? " Date: " + it.cn_date : ""}`;
  if (it.from || it.to) d += `\n${it.from || ""} TO ${it.to || ""}`;
  if (it.vehicle_type || it.weight || it.vehicle_no)
    d += `\nVehicle Type-${it.vehicle_type || ""}${it.weight ? " & " + it.weight : ""}${it.vehicle_no ? " & Vehicle No." + it.vehicle_no : ""}`;
  if (it.dc_no) d += `\nDC No.${it.dc_no}${it.dc_date ? " DC Date: " + it.dc_date : ""}`;
  return d;
};

export default function ManualBillForm({ customers, onCreated }) {
  const [customerId, setCustomerId] = useState("");
  const [billNo, setBillNo] = useState("");
  const [consignor, setConsignor] = useState("");
  const [consignee, setConsignee] = useState("");
  const [office, setOffice] = useState("Chennai");
  const [issueDate, setIssueDate] = useState(today());
  const [billingType, setBillingType] = useState("GST");
  const [taxPercent, setTaxPercent] = useState(18);
  const [unloadingCharge, setUnloadingCharge] = useState(0);
  const [haltingCharge, setHaltingCharge] = useState(0);
  const [loadingCharge, setLoadingCharge] = useState(0);
  const [labourUnionCharge, setLabourUnionCharge] = useState(0);
  const [loadingPointHalting, setLoadingPointHalting] = useState(0);
  const [craneCharge, setCraneCharge] = useState(0);
  const [vehicleReturnCharge, setVehicleReturnCharge] = useState(0);
  const [extraDeliveryCharge, setExtraDeliveryCharge] = useState(0);
  const [billDescription, setBillDescription] = useState("");
  const [billingAddress, setBillingAddress] = useState("");
  const [items, setItems] = useState([newItem()]);
  const [saving, setSaving] = useState(false);

  const customer = customers.find((c) => c.id === customerId);
  useEffect(() => {
    if (!customer) return;
    if (String(customer.billing_customer_address || "").trim()) {
      setBillingAddress(customer.billing_customer_address.trim());
      return;
    }
    const part = String(customer.address || "").split(",").map((s) => s.trim()).filter(Boolean).pop() || customer.state || "";
    const pin = String(customer.pincode || "").trim();
    setBillingAddress(pin && part && !part.includes(pin) ? `${part}-${pin}` : part || pin);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId]);
  const isRcm = billingType === "RCM";
  const effectiveTax = isRcm ? 0 : Number(taxPercent) || 0;
  const freightSubtotal = useMemo(() => items.reduce((s, i) => s + (Number(i.amount) || 0), 0), [items]);
  const extras = (Number(unloadingCharge) || 0) + (Number(haltingCharge) || 0) + (Number(loadingCharge) || 0) + (Number(labourUnionCharge) || 0) + (Number(loadingPointHalting) || 0) + (Number(craneCharge) || 0) + (Number(vehicleReturnCharge) || 0) + (Number(extraDeliveryCharge) || 0);
  const subtotal = freightSubtotal + extras;
  const tax = (subtotal * effectiveTax) / 100;
  const total = subtotal + tax;

  const setItem = (id, key, val) =>
    setItems((arr) => arr.map((it) => (it._id === id ? { ...it, [key]: val } : it)));
  const addItem = () => setItems((a) => [...a, newItem()]);
  const removeItem = (id) => setItems((a) => (a.length > 1 ? a.filter((it) => it._id !== id) : a));

  const create = async () => {
    if (!customerId) {
      toast.error("Please select a Bill To customer");
      return;
    }
    if (subtotal <= 0) {
      toast.error("Enter at least one line amount");
      return;
    }
    setSaving(true);
    try {
      const res = await api.post("/invoices", {
        invoice_no: billNo.trim(),
        customer_id: customerId,
        customer_name: customer ? customer.name : "",
        consignor,
        consignee,
        office,
        from_place: items[0]?.from || "",
        destination: items[0]?.to || "",
        lr_no: items[0]?.cn_no || "",
        lr_date: items[0]?.cn_date || "",
        issue_date: issueDate,
        tax_percent: effectiveTax,
        rcm: isRcm,
        unloading_charge: Number(unloadingCharge) || 0,
        halting_charge: Number(haltingCharge) || 0,
        loading_charge: Number(loadingCharge) || 0,
        labour_union_charge: Number(labourUnionCharge) || 0,
        loading_point_halting_charge: Number(loadingPointHalting) || 0,
        crane_charge: Number(craneCharge) || 0,
        vehicle_return_charge: Number(vehicleReturnCharge) || 0,
        extra_delivery_charge: Number(extraDeliveryCharge) || 0,
        bill_description: billDescription,
        billing_customer_address: billingAddress,
        items: items.map((it) => ({
          description: composeDesc(it),
          quantity: 1,
          rate: Number(it.amount) || 0,
          branch: office,
          lr_no: it.cn_no,
          lr_date: it.cn_date,
          from_place: it.from,
          destination: it.to,
          invoice_no: it.inv_no,
          invoice_date: it.inv_date,
          vehicle_no: it.vehicle_no,
          vehicle_type: it.vehicle_type,
          weight: it.weight,
        })),
      });
      toast.success(`Bill ${res.data.invoice_no} created (${inr2(res.data.total)})`, {
        description: "Do you want to print it now?",
        duration: 9000,
        action: { label: "Print Bill", onClick: () => { api.post(`/invoices/${res.data.id}/mark-printed`).catch(() => {}); printInvoice(res.data, customer); } },
      });
      setItems([newItem()]);
      setConsignor("");
      setConsignee("");
      setBillNo("");
      setBillingAddress("");
      setUnloadingCharge(0);
      setHaltingCharge(0);
      setLoadingCharge(0);
      setLabourUnionCharge(0);
      setLoadingPointHalting(0);
      setCraneCharge(0);
      setBillDescription("");
      onCreated && onCreated();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <Card className="border-border">
        <CardContent className="grid gap-4 p-6 sm:grid-cols-3">
          <div className="grid gap-1.5 sm:col-span-2">
            <Label>Bill To (customer)</Label>
            <Select value={customerId} onValueChange={setCustomerId}>
              <SelectTrigger data-testid="manual-bill-customer"><SelectValue placeholder="Select customer" /></SelectTrigger>
              <SelectContent>
                {customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
            {customer && (
              <p className="text-xs text-muted-foreground">
                {customer.address || "No address on file"} {customer.gstin ? `• GSTIN: ${customer.gstin}` : ""}
              </p>
            )}
          </div>
          <div className="grid gap-1.5">
            <Label>Bill No.</Label>
            <Input value={billNo} onChange={(e) => setBillNo(e.target.value)} placeholder="Leave blank to auto-generate" data-testid="manual-bill-no" />
          </div>
          <div className="grid gap-1.5">
            <Label>Bill Date</Label>
            <Input type="date" value={issueDate} onChange={(e) => setIssueDate(e.target.value)} data-testid="manual-bill-date" />
          </div>
          <div className="grid gap-1.5">
            <Label>Consignor Name</Label>
            <Input value={consignor} onChange={(e) => setConsignor(e.target.value)} placeholder="Consignor" data-testid="manual-consignor" />
          </div>
          <div className="grid gap-1.5">
            <Label>Consignee Name</Label>
            <Input value={consignee} onChange={(e) => setConsignee(e.target.value)} placeholder="Consignee" data-testid="manual-consignee" />
          </div>
          <div className="grid gap-1.5">
            <Label>Branch</Label>
            <Select value={office} onValueChange={setOffice}>
              <SelectTrigger data-testid="manual-office"><SelectValue /></SelectTrigger>
              <SelectContent>
                {["Chennai", "Tada", "Hyderabad", "Jaipur"].map((o) => (
                  <SelectItem key={o} value={o}>{o}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid gap-1.5">
            <Label>Billing Type</Label>
            <Select value={billingType} onValueChange={setBillingType}>
              <SelectTrigger data-testid="manual-billing-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="GST">GST (add tax)</SelectItem>
                <SelectItem value="RCM">RCM (no GST)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {!isRcm && (
            <div className="grid gap-1.5">
              <Label>Tax %</Label>
              <Input type="number" value={taxPercent} onChange={(e) => setTaxPercent(e.target.value)} data-testid="manual-tax" />
            </div>
          )}
          <div className="grid gap-1.5 sm:col-span-3">
            <Label>Description (prints above the items table, optional)</Label>
            <Textarea value={billDescription} onChange={(e) => setBillDescription(e.target.value)} placeholder="Type any description to show on the bill" data-testid="manual-bill-description" rows={2} />
          </div>
          <div className="grid gap-1.5 sm:col-span-3">
            <Label>Billing Customer Address (shown in Outstanding Report)</Label>
            <Input value={billingAddress} onChange={(e) => setBillingAddress(e.target.value)} placeholder="e.g. Sricity-517646" data-testid="manual-billing-customer-address" />
          </div>
        </CardContent>
      </Card>

      <div className="space-y-4">
        {items.map((it, idx) => (
          <Card key={it._id} className="border-border" data-testid="manual-line-item">
            <CardContent className="p-5">
              <div className="mb-3 flex items-center justify-between">
                <span className="text-[11px] font-semibold uppercase tracking-[0.15em] text-primary">Freight Line #{idx + 1}</span>
                <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => removeItem(it._id)} data-testid="manual-remove-item">
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
              <div className="grid gap-3 sm:grid-cols-4">
                <div className="grid gap-1.5"><Label>CN No.</Label><Input value={it.cn_no} onChange={(e) => setItem(it._id, "cn_no", e.target.value)} placeholder="3077" /></div>
                <div className="grid gap-1.5"><Label>CN Date</Label><Input type="date" value={it.cn_date} onChange={(e) => setItem(it._id, "cn_date", e.target.value)} /></div>
                <div className="grid gap-1.5"><Label>From</Label><Input value={it.from} onChange={(e) => setItem(it._id, "from", e.target.value)} placeholder="Theni" /></div>
                <div className="grid gap-1.5"><Label>To</Label><Input value={it.to} onChange={(e) => setItem(it._id, "to", e.target.value)} placeholder="Puduvoil" /></div>
                <div className="grid gap-1.5"><Label>Vehicle Type</Label><Input value={it.vehicle_type} onChange={(e) => setItem(it._id, "vehicle_type", e.target.value)} placeholder="40 Feet Trailer" /></div>
                <div className="grid gap-1.5"><Label>Weight</Label><Input value={it.weight} onChange={(e) => setItem(it._id, "weight", e.target.value)} placeholder="16 MT" /></div>
                <div className="grid gap-1.5"><Label>Vehicle No.</Label><Input value={it.vehicle_no} onChange={(e) => setItem(it._id, "vehicle_no", e.target.value)} placeholder="TN 88 B 0601" /></div>
                <div className="grid gap-1.5"><Label>Invoice No.</Label><Input value={it.inv_no} onChange={(e) => setItem(it._id, "inv_no", e.target.value)} placeholder="Goods invoice no." data-testid="manual-item-inv-no" /></div>
                <div className="grid gap-1.5"><Label>Invoice Date</Label><Input type="date" value={it.inv_date} onChange={(e) => setItem(it._id, "inv_date", e.target.value)} /></div>
                <div className="grid gap-1.5"><Label>Amount (₹)</Label><Input type="number" value={it.amount} onChange={(e) => setItem(it._id, "amount", e.target.value)} data-testid="manual-item-amount" /></div>
                <div className="grid gap-1.5"><Label>DC No.</Label><Input value={it.dc_no} onChange={(e) => setItem(it._id, "dc_no", e.target.value)} placeholder="RP/26-27/07/01" /></div>
                <div className="grid gap-1.5"><Label>DC Date</Label><Input type="date" value={it.dc_date} onChange={(e) => setItem(it._id, "dc_date", e.target.value)} /></div>
              </div>
            </CardContent>
          </Card>
        ))}
        <Button variant="outline" onClick={addItem} data-testid="manual-add-item">
          <Plus className="mr-2 h-4 w-4" /> Add Freight Line
        </Button>
      </div>

      <Card className="border-border">
        <CardContent className="flex flex-wrap items-center justify-between gap-4 p-6">
          <div className="space-y-3 text-sm">
            <div className="flex flex-wrap gap-4">
              <div className="grid gap-1.5">
                <Label>Unloading Charge (₹)</Label>
                <Input type="number" className="w-[150px]" value={unloadingCharge} onChange={(e) => setUnloadingCharge(e.target.value)} data-testid="manual-unloading-charge" />
              </div>
              <div className="grid gap-1.5">
                <Label>Loading Charge (₹)</Label>
                <Input type="number" className="w-[150px]" value={loadingCharge} onChange={(e) => setLoadingCharge(e.target.value)} data-testid="manual-loading-charge" />
              </div>
              <div className="grid gap-1.5">
                <Label>Unloading Point Halting Charge (₹)</Label>
                <Input type="number" className="w-[190px]" value={haltingCharge} onChange={(e) => setHaltingCharge(e.target.value)} data-testid="manual-halting-charge" />
              </div>
              <div className="grid gap-1.5">
                <Label>Labour Union Charge (₹)</Label>
                <Input type="number" className="w-[170px]" value={labourUnionCharge} onChange={(e) => setLabourUnionCharge(e.target.value)} data-testid="manual-labour-union-charge" />
              </div>
              <div className="grid gap-1.5">
                <Label>Loading Point Halting Charge (₹)</Label>
                <Input type="number" className="w-[190px]" value={loadingPointHalting} onChange={(e) => setLoadingPointHalting(e.target.value)} data-testid="manual-loading-point-halting-charge" />
              </div>
              <div className="grid gap-1.5">
                <Label>Crane Charge (₹)</Label>
                <Input type="number" className="w-[150px]" value={craneCharge} onChange={(e) => setCraneCharge(e.target.value)} data-testid="manual-crane-charge" />
              </div>
              <div className="grid gap-1.5">
                <Label>Vehicle Return Charge (₹)</Label>
                <Input type="number" className="w-[170px]" value={vehicleReturnCharge} onChange={(e) => setVehicleReturnCharge(e.target.value)} data-testid="manual-vehicle-return-charge" />
              </div>
              <div className="grid gap-1.5">
                <Label>Extra Delivery Charge (₹)</Label>
                <Input type="number" className="w-[170px]" value={extraDeliveryCharge} onChange={(e) => setExtraDeliveryCharge(e.target.value)} data-testid="manual-extra-delivery-charge" />
              </div>
            </div>
            <div className="space-y-1">
              <div className="flex gap-6"><span className="text-muted-foreground">Subtotal</span><span className="tabular-nums">{inr2(freightSubtotal)}</span></div>
              {Number(unloadingCharge) > 0 && <div className="flex gap-6"><span className="text-muted-foreground">Unloading Charge</span><span className="tabular-nums">{inr2(Number(unloadingCharge) || 0)}</span></div>}
              {Number(loadingCharge) > 0 && <div className="flex gap-6"><span className="text-muted-foreground">Loading Charge</span><span className="tabular-nums">{inr2(Number(loadingCharge) || 0)}</span></div>}
              {Number(haltingCharge) > 0 && <div className="flex gap-6"><span className="text-muted-foreground">Unloading Point Halting Charge</span><span className="tabular-nums">{inr2(Number(haltingCharge) || 0)}</span></div>}
              {Number(labourUnionCharge) > 0 && <div className="flex gap-6"><span className="text-muted-foreground">Labour Union Charge</span><span className="tabular-nums">{inr2(Number(labourUnionCharge) || 0)}</span></div>}
              {Number(loadingPointHalting) > 0 && <div className="flex gap-6"><span className="text-muted-foreground">Loading Point Halting Charge</span><span className="tabular-nums">{inr2(Number(loadingPointHalting) || 0)}</span></div>}
              {Number(craneCharge) > 0 && <div className="flex gap-6"><span className="text-muted-foreground">Crane Charge</span><span className="tabular-nums">{inr2(Number(craneCharge) || 0)}</span></div>}
              {Number(vehicleReturnCharge) > 0 && <div className="flex gap-6"><span className="text-muted-foreground">Vehicle Return Charge</span><span className="tabular-nums">{inr2(Number(vehicleReturnCharge) || 0)}</span></div>}
              {Number(extraDeliveryCharge) > 0 && <div className="flex gap-6"><span className="text-muted-foreground">Extra Delivery Charge</span><span className="tabular-nums">{inr2(Number(extraDeliveryCharge) || 0)}</span></div>}
              <div className="flex gap-6"><span className="text-muted-foreground">{isRcm ? "GST (RCM — payable by consignee)" : `Tax (${taxPercent || 0}%)`}</span><span className="tabular-nums">{inr2(tax)}</span></div>
              <div className="flex gap-6 font-display text-lg font-bold"><span>Total Due</span><span className="tabular-nums" data-testid="manual-bill-total">{inr2(total)}</span></div>
            </div>
          </div>
          <Button size="lg" onClick={create} disabled={saving} data-testid="create-manual-bill-btn">
            <FileText className="mr-2 h-4 w-4" />
            {saving ? "Creating…" : "Create & Print Bill"}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
